import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  DownloadOutlined,
  FilePdfOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { captureOutboundLinkClick } from '../utils/analytics';

function clampPage(page, pageCount) {
  return Math.min(pageCount, Math.max(1, page));
}

function buildPdfViewerSrc(pdfUrl, page) {
  return `${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0&page=${page}&view=FitH`;
}

function buildSlideImageSrc(basePath, page, extension = 'webp') {
  return `${basePath}/slide-${String(page).padStart(2, '0')}.${extension}`;
}

export default function SlideDeckViewer({
  title,
  description,
  pdfUrl,
  slideImageBasePath,
  thumbnailImageBasePath,
  imageExtension = 'webp',
  pageCount = 0,
  analyticsEventName,
  analyticsPlacement,
  pending = false,
  pendingTitle = 'Slides coming soon',
  pendingDescription = 'The PDF viewer will appear here after the slide deck is added to the site.',
  note,
  className = '',
}) {
  const headingId = useId();
  const viewerRef = useRef(null);
  const hasDeck = Boolean(!pending && pdfUrl && pageCount > 0);
  const hasImageDeck = Boolean(hasDeck && slideImageBasePath);
  const [page, setPage] = useState(1);
  const pages = useMemo(
    () => Array.from({ length: pageCount }, (_, index) => index + 1),
    [pageCount]
  );

  useEffect(() => {
    setPage(1);
  }, [pdfUrl, pageCount]);

  useEffect(() => {
    if (!hasDeck) return undefined;

    const handleKeyDown = (event) => {
      if (!viewerRef.current?.contains(document.activeElement)) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setPage((current) => clampPage(current - 1, pageCount));
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setPage((current) => clampPage(current + 1, pageCount));
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hasDeck, pageCount]);

  const viewerSrc = hasDeck ? buildPdfViewerSrc(pdfUrl, page) : '';
  const slideImageSrc = hasImageDeck ? buildSlideImageSrc(slideImageBasePath, page, imageExtension) : '';
  const progress = hasDeck ? `${(page / pageCount) * 100}%` : '0%';

  const goToPage = (nextPage) => {
    if (!hasDeck) return;
    setPage(clampPage(nextPage, pageCount));
  };

  const handleOpenPdf = () => {
    if (analyticsEventName && pdfUrl) {
      captureOutboundLinkClick(analyticsEventName, pdfUrl, { placement: analyticsPlacement });
    }
  };

  return (
    <section aria-labelledby={headingId} className={className}>
      <div className="mx-auto max-w-[1180px]">
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 id={headingId} className="text-2xl font-semibold tracking-normal text-[#1d1d1f]">
              {title}
            </h2>
            {description && (
              <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-[#6e6e73]">
                {description}
              </p>
            )}
          </div>
          {hasDeck ? (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleOpenPdf}
              className="inline-flex w-fit items-center justify-center gap-2 rounded-full border border-[#1d1d1f] px-4 py-2 text-sm font-semibold text-[#1d1d1f] transition hover:bg-[#1d1d1f] hover:text-white"
            >
              <DownloadOutlined aria-hidden="true" />
              Open full PDF
            </a>
          ) : (
            <span className="w-fit rounded-full bg-[#f5f5f7] px-3 py-1 text-xs font-semibold text-[#6e6e73]">
              PDF pending
            </span>
          )}
        </div>

        {hasDeck ? (
          <div
            ref={viewerRef}
            tabIndex={0}
            className="overflow-hidden rounded-lg border border-[#d6d6db] bg-white shadow-[0_18px_45px_rgba(15,23,42,0.08)] outline-none focus:ring-2 focus:ring-emerald-500/50"
          >
            <div className="grid lg:grid-cols-[164px_minmax(0,1fr)]">
              <div className="min-w-0 border-b border-[#e5e5ea] bg-[#f5f5f7] p-3 lg:max-h-[680px] lg:overflow-y-auto lg:border-b-0 lg:border-r">
                <ol className="flex w-full gap-2 overflow-x-auto lg:grid lg:overflow-x-visible">
                  {pages.map((pageNumber) => {
                    const active = pageNumber === page;
                    const thumbSrc = hasImageDeck
                      ? buildSlideImageSrc(thumbnailImageBasePath || slideImageBasePath, pageNumber, imageExtension)
                      : '';
                    return (
                      <li key={pageNumber} className="min-w-[112px] shrink-0 lg:min-w-0 lg:shrink">
                        <button
                          type="button"
                          aria-label={`Go to slide ${pageNumber}`}
                          aria-current={active ? 'page' : undefined}
                          onClick={() => goToPage(pageNumber)}
                          className={`w-full rounded-lg border p-2 text-left transition ${
                            active
                              ? 'border-emerald-600 bg-white shadow-sm'
                              : 'border-transparent bg-transparent hover:border-[#d6d6db] hover:bg-white'
                          }`}
                        >
                          {hasImageDeck && (
                            <span className="mb-2 hidden overflow-hidden rounded bg-[#111111] sm:block">
                              <img
                                src={thumbSrc}
                                alt=""
                                loading="lazy"
                                className="aspect-video w-full object-cover"
                              />
                            </span>
                          )}
                          <span className={`block font-mono text-[12px] font-semibold ${active ? 'text-emerald-700' : 'text-[#86868b]'}`}>
                            {String(pageNumber).padStart(2, '0')}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ol>
              </div>

              <div className="min-w-0 bg-[#111111]">
                <div className="bg-[#1d1d1f] p-3 sm:p-5">
                  <div className="mx-auto max-w-[960px] overflow-hidden rounded-lg bg-black shadow-[0_24px_70px_rgba(0,0,0,0.32)]">
                    {hasImageDeck ? (
                      <img
                        key={slideImageSrc}
                        src={slideImageSrc}
                        alt={`${title} slide ${page}`}
                        className="block w-full bg-black"
                      />
                    ) : (
                      <iframe
                        key={viewerSrc}
                        src={viewerSrc}
                        title={`${title} slide ${page}`}
                        className="h-[360px] w-full border-0 bg-black sm:aspect-[16/9] sm:h-auto"
                      />
                    )}
                    <div className="flex items-center justify-between border-t border-white/10 bg-[#111111] px-3 py-2 text-white">
                      <span className="text-xs font-semibold">
                        {page} / {pageCount}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          aria-label="Previous slide"
                          title="Previous slide"
                          onClick={() => goToPage(page - 1)}
                          disabled={page === 1}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-35"
                        >
                          <LeftOutlined aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          aria-label="Next slide"
                          title="Next slide"
                          onClick={() => goToPage(page + 1)}
                          disabled={page === pageCount}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-35"
                        >
                          <RightOutlined aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="h-1 bg-white/10">
                  <div className="h-full bg-emerald-400 transition-all duration-200" style={{ width: progress }} />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex min-h-[360px] items-center justify-center rounded-lg border border-dashed border-[#c7c7cc] bg-[#fafafa] px-6 text-center">
            <div>
              <FilePdfOutlined aria-hidden="true" className="mb-4 text-4xl text-[#86868b]" />
              <p className="text-lg font-semibold text-[#1d1d1f]">{pendingTitle}</p>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-[#6e6e73]">
                {pendingDescription}
              </p>
            </div>
          </div>
        )}

        {note && (
          <p className="mt-4 text-[13px] leading-relaxed text-[#86868b]">
            {note}
          </p>
        )}
      </div>
    </section>
  );
}

export { buildPdfViewerSrc, buildSlideImageSrc };
